import { Formik } from "formik";

import { useGetPricingByIdQuery, useUpdatePricingMutation } from "@sso/apis/pricing";
import { useGetSoftwareLicenceById } from "@sso/apis/softwareLicences/hooks";
import { DialogActionButtons, DialogBody, DialogHeader } from "@sso/components/Dialogs/components";
import { useAppSelector } from "@sso/hooks/useRedux";
import { selectDialog } from "@sso/store/slices/dialog";
import { selectPricingSoftwareLicenceId } from "@sso/store/slices/pricing";
import {
  LicenceType,
  PaymentType,
  Pricing,
  PricingFormModel,
  PricingKeys,
  PricingRule,
  PricingRuleCreateRequest,
  PricingRuleKeys,
  PricingSummaryKeys,
  PricingUpdateRequest,
  SoftwareLicenceSummary,
  SoftwareLicenceSummaryKeys,
} from "@sso/types";
import { CustomForm } from "@sso/ui/CustomForm";

import PricingForm from "./pricingForm";
import { pricingSchemeValidation } from "./schema";

const PricingEditDialog = () => {
  const { id } = useAppSelector(selectDialog);
  if (!id) {
    throw new Error("PricingEditDialog: id is null");
  }
  const handleSubmit = (values: PricingFormModel) => {
    if (softwareLicence!.licenceType === LicenceType.API) {
      values[PricingKeys.PRICING_RULES] = values.apiPricingRules?.reduce((acc: any, curr: any) => {
        const pricingRules = curr.pricingRules.map((rule: any) => {
          return { ...rule, billableOperationId: curr.billableOperationId };
        });
        return [...acc, ...pricingRules];
      }, []);
    }

    var request = {
      [PricingSummaryKeys.PRICING_ID]: values[PricingSummaryKeys.PRICING_ID],
      [PricingSummaryKeys.DATE_OF_EFFECT]: values[PricingSummaryKeys.DATE_OF_EFFECT],
      [PricingKeys.PRICING_RULES]: values[PricingKeys.PRICING_RULES]?.map(rule => ({
        ...rule,
        currencyCode: "GBP",
      })),
      softwareLicenceId: values.softwareLicenceId,
    } as PricingUpdateRequest;

    updatePricing(request);
  };

  const [updatePricing, { isLoading: updateLoading }] = useUpdatePricingMutation();

  const { data, isSuccess } = useGetPricingByIdQuery(id);

  const softwareLicenceId = useAppSelector(selectPricingSoftwareLicenceId);
  const softwareLicence = useGetSoftwareLicenceById(softwareLicenceId);

  const mapToRuleRequests = (rules: PricingRule[]) => {
    const ruleRequests = rules
      .map((rule: PricingRule) => {
        return {
          ...rule,
          isLast: rule[PricingRuleKeys.MIN_RANGE] != null && rule[PricingRuleKeys.MAX_RANGE] == null,
        };
      })
      .map((r: PricingRuleCreateRequest) => {
        if (r.isLast) {
          delete r.maxRange;
        }
        return r;
      })
      .sort((a, b) => a.minRange! - b.minRange!);

    return ruleRequests;
  };

  const getInitialValues = (data: Pricing, sl: SoftwareLicenceSummary) => {
    const licenceType = sl.licenceType;
    const paymentType = sl.paymentType;

    const pricingForm = {
      [PricingSummaryKeys.DATE_OF_EFFECT]: data[PricingSummaryKeys.DATE_OF_EFFECT],
      [PricingKeys.PRICING_RULES]: mapToRuleRequests(data[PricingKeys.PRICING_RULES] ?? []),
      licenceType: licenceType as LicenceType,
      paymentType: paymentType as PaymentType,
      softwareLicenceId: sl.softwareLicenceId,
      apiPricingRules: [],
      pricingId: data[PricingKeys.PRICING_ID],
    } as PricingFormModel;

    return pricingForm;
  };

  return (
    <>
      <DialogHeader title={"Edit Pricing"} />
      {data && softwareLicence && (
        <Formik
          onSubmit={handleSubmit}
          initialValues={getInitialValues(data, softwareLicence) as PricingFormModel}
          validationSchema={pricingSchemeValidation}
        >
          {() => (
            <CustomForm width="100%" overflow="hidden">
              <DialogBody>
                {isSuccess && data && softwareLicence && (
                  <>
                    <PricingForm licenceType={softwareLicence[SoftwareLicenceSummaryKeys.LICENCE_TYPE]} />
                  </>
                )}
                <DialogActionButtons loading={updateLoading} buttonTitle={"Edit"} />
              </DialogBody>
            </CustomForm>
          )}
        </Formik>
      )}
    </>
  );
};

export default PricingEditDialog;
